import React from 'react';
import { POINT_STYLES, TRANSLATIONS } from '../../../constants';
import { CurveType, SpecialPointType, StartingType } from '../../../enums';
import { useAppSelector, useTranslate } from '../../../hooks/common';
import { Curve, PointStyle } from '../../../models';
import { disassambleSpecialPoints, getCurveColor, getPowerUnit } from '../../../services';
import CurveLabelCircle from '../../common/CurveLabelCircle';

interface LegendProps {
    curves: Curve[];
    visibleCurves: Curve[];
    onCurveLabelClick: (type: CurveType) => void;
}

const Legend = ({ curves, visibleCurves, onCurveLabelClick }: LegendProps) => {
    const translate = useTranslate();
    const mesRequest = useAppSelector(state => state.motor.mesRequest);
    const isStartingTypeDOL = mesRequest.commonData.startingType === StartingType.DOL;
    const disabledCurveTypes = useAppSelector(state => state.diagram.disabledCurveTypes);

    const getCurveLabelCircle = (curveType: CurveType, isStartingTypeDOL: boolean) => {
        const color = getCurveColor(curveType, isStartingTypeDOL);
        let className = 'circle ';

        switch (curveType) {
            case CurveType.TorqueOverSpeedWithSpecificVoltage:
            case CurveType.CurrentOverSpeedWithSpecificVoltage:
            case CurveType.CurrentOverTimeWithDWithSpecificVoltage:
            case CurveType.CurrentOverTimeWithYWithSpecificVoltage:
            case CurveType.MotorShaftPowerWithSpecificVoltage:
            case CurveType.StartUpTimeWithSpecificVoltage:
            case CurveType.StartUpTimeWithYD:
                className += 'dashed';
                break;
            case CurveType.AccelerationTorqueWithSpecificVoltage:
                className += isStartingTypeDOL ? 'dashed' : 'solid';
                break;
            default:
                className += 'solid';
                break;
        }

        return <CurveLabelCircle style={{ stroke: color }} className={className} />;
    };

    const renderSpecialPointLabel = (lineColor: string, specialPointType: SpecialPointType) => {
        const { shape, backgroundColor, borderColor } = POINT_STYLES.get(specialPointType) as PointStyle;
        const powerUnit = getPowerUnit(mesRequest.unitSystem);

        return (
            <div key={specialPointType} className='special-point-label'>
                <div className='sign'>
                    <div className='line' style={{ backgroundColor: lineColor }} />
                    <div className={`shape ${shape}`} style={{ backgroundColor, borderColor }} />
                </div>
                {translate((TRANSLATIONS.diagram.legend.specialPointLabel as never)[SpecialPointType[specialPointType].toLowerCase()], powerUnit)}
            </div>
        );
    };

    return (
        <div className='legend'>
            <div className='curve-labels'>
                {curves.map(x =>
                    <div key={x.curveType} className={`curve-label ${disabledCurveTypes.some(y => y === x.curveType) ? 'disabled' : ''}`}
                        onClick={() => onCurveLabelClick(x.curveType)}>
                        {getCurveLabelCircle(x.curveType, isStartingTypeDOL)}
                        {x.label}
                    </div>
                )}
            </div>
            <div className='special-point-labels'>
                {disassambleSpecialPoints(visibleCurves)
                    .filter(x => x.specialPointTypes)
                    .map(x =>
                        x.specialPointTypes.filter((item, pos, self) => self.indexOf(item) == pos).map(y => renderSpecialPointLabel(x.borderColor, y))
                    )}
            </div>
        </div>
    );
};

export default Legend;
